import React, { useState, useMemo } from 'react';
import Carousel from 'react-material-ui-carousel';
import { Box, Typography, Paper, IconButton, CircularProgress } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { IMedia } from '@/interfaces';

export interface ProductImageCarouselProps {
  medias?: IMedia[];
  initialIndex?: number;
  height?: number | string;
  autoPlay?: boolean;
  showThumbnails?: boolean;
  onMediaClick?: (media: IMedia, index: number) => void;
}

const ProductImageCarousel: React.FC<ProductImageCarouselProps> = React.memo(({
  medias,
  initialIndex = 0,
  height = 400,
  autoPlay = false,
  showThumbnails = true,
  onMediaClick,
}) => {
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);
  const [loadingImages, setLoadingImages] = useState<Record<number, boolean>>({});
  const [zoom, setZoom] = useState<number>(1);

  // Handle case when no product or media is available
  if (!medias?.length) {
    return (
      <Paper 
        elevation={0} 
        variant="outlined" 
        sx={{ 
          width: '100%', 
          height: height, 
          mb: 2, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          bgcolor: '#f5f5f5'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No product images available
        </Typography>
      </Paper>
    );
  }

  // Process media items
  const items = medias
    .map((media: IMedia, index: number) => {
      // Set initial loading state for each image
      if (loadingImages[index] === undefined) {
        setLoadingImages(prev => ({ ...prev, [index]: true }));
      }

      if (!media.cdn_url) {
        return null;
      }

      return {
        image: media.cdn_url,
        type: media.type,
        originalMedia: media,
        index: index,
      };
    })
    .filter(Boolean); // Filter out null values

  if (items.length === 0) {
    return (
      <Paper 
        elevation={0} 
        variant="outlined" 
        sx={{ 
          width: '100%', 
          height: height, 
          mb: 2, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          bgcolor: '#f5f5f5'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No valid media found for this product
        </Typography>
      </Paper>
    );
  }

  const handleImageLoad = (index: number) => {
    setLoadingImages(prev => ({ ...prev, [index]: false }));
  };

  const handleCarouselChange = (index: number | undefined) => {
    if (index !== undefined) {
      setCurrentIndex(index);
      setZoom(1);
    }
  };

  const toggleFullscreen = () => {
    setFullscreen(!fullscreen);
    // Reset zoom when toggling fullscreen
    setZoom(1);
  };

  const zoomIn = () => {
    if (zoom < 2) setZoom(prev => Math.min(prev + 0.25, 2));
  };

  const zoomOut = () => {
    if (zoom > 0.5) setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleMediaClick = (item: typeof items[0]) => {
    if (onMediaClick && item) {
      onMediaClick(item.originalMedia, item.index);
    } else if (item?.type !== 'video') {
      toggleFullscreen();
    }
  };

  // Calculate fullscreen dimensions for responsive behavior
  const carouselHeight = fullscreen ? '80vh' : typeof height === 'number' 
    ? showThumbnails && items.length > 1 
      ? `${height - 90}px` // Reserve space for thumbnails when they're shown
      : `${height}px` 
    : height;

  // Move any expensive calculations inside useMemo
  const processedImages = useMemo(() => 
    items.filter((item): item is NonNullable<typeof item> => item !== null)
      .map((item) => ({
        image: item.image,
        type: item.type,
        originalMedia: item.originalMedia!,  // Assert non-null since we filtered nulls
        index: item.index
      })),
    [items]
  );

  return (
    <Box 
      sx={{ 
        position: fullscreen ? 'fixed' : 'relative',
        top: fullscreen ? 0 : 'auto',
        left: fullscreen ? 0 : 'auto',
        right: fullscreen ? 0 : 'auto',
        bottom: fullscreen ? 0 : 'auto',
        width: '100%',
        height: fullscreen ? '100vh' : height,
        mb: 2,
        zIndex: fullscreen ? 9999 : 'auto',
        backgroundColor: fullscreen ? 'rgba(0, 0, 0, 0.9)' : 'transparent',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: fullscreen ? '16px' : 0
      }}
    >
      {fullscreen && (
        <IconButton 
          onClick={toggleFullscreen}
          sx={{ 
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            },
            zIndex: 10000
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      
      <Carousel
        height={carouselHeight}
        autoPlay={autoPlay}
        animation="fade"
        indicators={true}
        navButtonsAlwaysVisible={true}
        fullHeightHover={false}
        index={currentIndex}
        onChange={handleCarouselChange}
        indicatorContainerProps={{
          style: {
            marginTop: '10px',
            textAlign: 'center'
          }
        }}
        indicatorIconButtonProps={{
          style: {
            padding: '5px',
            color: '#bdbdbd'
          }
        }}
        activeIndicatorIconButtonProps={{
          style: {
            color: '#2196f3'
          }
        }}
        navButtonsProps={{
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            color: '#494949',
            borderRadius: '50%',
            padding: '8px'
          }
        }}
        navButtonsWrapperProps={{
          style: {
            padding: '0 20px'
          }
        }}
        sx={{
          width: '100%',
          height: '100%',
          '& .MuiPaper-root': {
            backgroundColor: fullscreen ? 'transparent' : '#fff',
            boxShadow: fullscreen ? 'none' : '0px 2px 8px rgba(0, 0, 0, 0.1)',
            borderRadius: '4px',
            overflow: 'hidden',
          }
        }}
      >
        {processedImages.map((item: typeof items[0], i: number) => (
          <Paper 
            key={i} 
            elevation={0}
            sx={{ 
              height: '100%',
              position: 'relative',
              backgroundColor: fullscreen ? 'transparent' : '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 2
            }}
          >
            {loadingImages[item?.index ?? 0] && (
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <CircularProgress size={40} thickness={4} />
              </Box>
            )}
            
            {item?.type === "video" ? (
              <Box 
                component={"video"}
                src={item.image}
                sx={{ 
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  objectFit: 'contain'
                }}
                controls={true}
                onClick={() => handleMediaClick(item)}
              >
                <Box
                  component="img"
                  src={item.image}
                  alt={item.originalMedia.title}
                  onLoad={() => handleImageLoad(item.index)}
                  onError={() => handleImageLoad(item.index)}
                  sx={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%',
                    objectFit: 'contain',
                    opacity: loadingImages[item.index] ? 0 : 1,
                    transition: 'opacity 0.3s'
                  }}
                />
                <IconButton
                  aria-label="Play video"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)'
                    }
                  }}
                >
                  <PlayCircleOutlineIcon fontSize="large" />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{ 
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden'
                }}
              >
                <Box
                  component="img"
                  src={item?.image}
                  alt={item?.originalMedia?.title}
                  onLoad={() => handleImageLoad(item?.index ?? 0)}
                  onError={() => handleImageLoad(item?.index ?? 0)}
                  onClick={() => handleMediaClick(item)}
                  sx={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%',
                    objectFit: 'contain',
                    opacity: loadingImages[item?.index ?? 0] ? 0 : 1,
                    transition: 'opacity 0.3s, transform 0.3s',
                    transform: `scale(${zoom})`,
                    cursor: 'pointer'
                  }}
                />
              </Box>
            )}
            
            {!fullscreen && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8, 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <IconButton 
                  size="small"
                  aria-label="View fullscreen"
                  onClick={toggleFullscreen}
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                >
                  <FullscreenIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            
            {fullscreen && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  bottom: 16, 
                  right: 16, 
                  display: 'flex',
                  gap: 1
                }}
              >
                <IconButton 
                  onClick={zoomOut}
                  aria-label="Zoom out"
                  disabled={zoom <= 0.5}
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    color: zoom <= 0.5 ? '#bdbdbd' : '#494949',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                >
                  <ZoomOutIcon />
                </IconButton>
                <IconButton 
                  onClick={zoomIn}
                  aria-label="Zoom in"
                  disabled={zoom >= 2}
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    color: zoom >= 2 ? '#bdbdbd' : '#494949',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                >
                  <ZoomInIcon />
                </IconButton>
              </Box>
            )}
          </Paper>
        ))}
      </Carousel>
      
      {/* Thumbnail navigation for desktop */}
      {!fullscreen && showThumbnails && items.length > 1 && (
        <Box 
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            mt: 2,
            gap: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            justifyContent: 'center',
            padding: '8px 0',
            flexShrink: 0, // Prevent thumbnails from shrinking
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#bdbdbd',
              borderRadius: '6px',
            }
          }}
        >
          {items.map((item, i) => (
            <Box
              key={i}
              component="img"
              src={item?.image}
              alt={`Thumbnail ${i + 1}`}
              onClick={() => setCurrentIndex(i)}
              sx={{ 
                width: 60,
                height: 60,
                objectFit: 'cover',
                cursor: 'pointer',
                borderRadius: '4px',
                border: currentIndex === i ? '2px solid #2196f3' : '2px solid transparent',
                opacity: currentIndex === i ? 1 : 0.7,
                transition: 'all 0.2s',
                '&:hover': {
                  opacity: 1
                }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
});

// Default props for type safety and ease of use
ProductImageCarousel.defaultProps = {
  initialIndex: 0,
  height: 400,
  autoPlay: false,
  showThumbnails: true
};

export default ProductImageCarousel;